<template>
  <v-app :dark="darkMode">
    <v-toolbar
      app
      color="red"
      dark
      class="app-toolbar app-toolbar-responsive"
      :extension-height="56"
    >
      <v-toolbar-title
        class="headline text-uppercase d-flex align-center app-toolbar-title-wrap"
      >
        <span class="app-toolbar-logo-wrap">
          <img
            :src="require('./assets/rankster-logo-square.png')"
            alt="Rankster logo"
            class="app-toolbar-logo"
          />
        </span>
        <span class="app-toolbar-title-text">
          <span class="app-toolbar-brand">Project Rankster </span>
          <span class="font-weight-light app-toolbar-tagline">
            Information Based Rankings
          </span>
        </span>
      </v-toolbar-title>
      <template v-if="!stackToolbarSeason">
        <v-spacer class="app-toolbar-spacer" />
        <v-select
          v-model="uiSeasonModel"
          :items="seasonToolbarSelectItems"
          item-text="text"
          item-value="value"
          :loading="seasonsRefreshing"
          label="Season"
          hide-details
          dense
          outlined
          dark
          class="app-toolbar-season"
        />
      </template>
      <template v-else v-slot:extension>
        <div class="app-toolbar-extension-row">
          <v-select
            v-model="uiSeasonModel"
            :items="seasonToolbarSelectItems"
            item-text="text"
            item-value="value"
            :loading="seasonsRefreshing"
            label="Season"
            hide-details
            dense
            outlined
            dark
            class="app-toolbar-season app-toolbar-season--stacked"
          />
        </div>
      </template>
    </v-toolbar>

    <v-content>
      <router-view />
    </v-content>

    <!-- <Adsense
      data-ad-client="ca-pub-6178163766186653"
      data-ad-slot="1234567890">
    </Adsense> -->

    <v-footer height="auto">
      <v-card class="flex" flat tile color="red">
        <v-card-actions class="darken-3 justify-center">
          &copy;2019 — <strong>Kennected Software</strong>
        </v-card-actions>
      </v-card>
    </v-footer>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      bottom
    >
      {{ snackbar.text }}
      <v-btn dark text @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import {
  seasonFilterState,
  setSeasonFilter,
  fetchSeasonsThatHaveMatches,
  seasonShortToDisplay,
  currentSeasonLabelShort,
  mergeSeasonToolbarList,
} from "./utils/seasonFilter";

const DARK_STORAGE_KEY = "rankster-dark";

function getSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export default {
  name: "App",
  data() {
    const stored =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(DARK_STORAGE_KEY)
        : null;
    const useStored = stored === "true" || stored === "false";
    const initialDark = useStored ? stored === "true" : getSystemPrefersDark();
    return {
      userFirstName: "",
      user: {},
      darkMode: initialDark,
      snackbar: { show: false, text: "", color: "success" },
      unsubscribeSystemTheme: null,
      seasonToolbarItems: ["All"],
      seasonsRefreshing: false,
    };
  },
  computed: {
    userName: function () {
      const auth = getAuth();
      return auth.currentUser && auth.currentUser.Name;
    },
    uiSeasonModel: {
      get() {
        return seasonFilterState.selected;
      },
      set(v) {
        setSeasonFilter(v);
      },
    },
    seasonToolbarSelectItems() {
      return this.seasonToolbarItems.map((s) =>
        s === "All"
          ? { text: "All", value: "All" }
          : { text: seasonShortToDisplay(s), value: s }
      );
    },
  },
  mounted() {
    this.$root.$on("toast", (text, color) => {
      this.snackbar = { show: true, text, color: color || "success" };
    });
    this.$root.$on("rankster-seasons-changed", () => {
      this.refreshSeasonToolbarItems(true);
    });
    this.refreshSeasonToolbarItems(false);
    const mq =
      window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
    if (mq) {
      const onChange = () => {
        const stored = localStorage.getItem(DARK_STORAGE_KEY);
        if (stored !== "true" && stored !== "false") {
          this.darkMode = mq.matches;
        }
      };
      mq.addEventListener("change", onChange);
      this.unsubscribeSystemTheme = () =>
        mq.removeEventListener("change", onChange);
    }
  },
  beforeDestroy() {
    this.$root.$off("toast");
    this.$root.$off("rankster-seasons-changed");
    if (this.unsubscribeSystemTheme) this.unsubscribeSystemTheme();
  },
  methods: {
    async refreshSeasonToolbarItems(force) {
      this.seasonsRefreshing = true;
      try {
        const db = getFirestore();
        const items = await fetchSeasonsThatHaveMatches(db, {
          force: !!force,
        });
        this.seasonToolbarItems = mergeSeasonToolbarList(items);
        const cur = seasonFilterState.selected;
        if (cur && cur !== "All" && !this.seasonToolbarItems.includes(cur)) {
          setSeasonFilter(currentSeasonLabelShort());
        }
      } catch (_e) {
        const active = currentSeasonLabelShort();
        this.seasonToolbarItems = mergeSeasonToolbarList(["All", active]);
      } finally {
        this.seasonsRefreshing = false;
      }
    },
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      try {
        localStorage.setItem(
          DARK_STORAGE_KEY,
          this.darkMode ? "true" : "false"
        );
      } catch (_) {}
    },
  },
};
</script>

<style>
/*
 * Fixed circle: <img> as a flex item gets min-width: auto = intrinsic bitmap width,
 * which stretched the wrapper into an oval. Absolutely position the img inside a
 * fixed box so only 32×32 counts for layout.
 */
.app-toolbar-logo-wrap {
  position: relative;
  width: 32px;
  height: 32px;
  min-width: 32px;
  max-width: 32px;
  min-height: 32px;
  max-height: 32px;
  flex: 0 0 32px;
  margin-right: 8px;
  border-radius: 50%;
  overflow: hidden;
  align-self: center;
}
.app-toolbar-logo {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

/* Title row can shrink so it doesn’t collide with the season control */
.app-toolbar-title-wrap {
  min-width: 0 !important;
  flex: 1 1 auto;
  max-width: 100%;
}
.app-toolbar-title-text {
  min-width: 0;
  display: inline;
}
.app-toolbar-brand {
  white-space: nowrap;
}

.app-toolbar-spacer {
  flex-shrink: 1;
  min-width: 8px;
}

.app-toolbar-season {
  max-width: 168px;
  flex: 0 0 auto;
  margin-top: 10px;
}
.app-toolbar-extension-row {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 8px 4px;
  box-sizing: border-box;
}
.app-toolbar-season--stacked {
  max-width: none !important;
  width: 100%;
  margin-top: 0 !important;
  flex: 1 1 auto;
}
.app-toolbar-season .v-input__slot {
  border-color: rgba(255, 255, 255, 0.55) !important;
}
.app-toolbar-season .v-label,
.app-toolbar-season .v-select__selection {
  color: rgba(255, 255, 255, 0.95) !important;
}

@media (max-width: 720px) {
  .app-toolbar-tagline {
    display: none;
  }
}

@media (max-width: 960px) {
  .app-toolbar-responsive .headline {
    font-size: 1.05rem !important;
    line-height: 1.25 !important;
  }
}

@media (min-width: 601px) and (max-width: 900px) {
  .app-toolbar-responsive .app-toolbar-brand {
    max-width: 36vw;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: bottom;
  }
  .app-toolbar-season {
    max-width: 130px;
  }
}
</style>
